import { limit, orderBy, query, where } from 'firebase/firestore'
import {
  Batch,
  DocRef,
  Glossary,
  GlossaryDraft,
  GlossaryTerms,
  Term,
  TermWithGlossaryInfo,
  Timestamp,
  getDoc,
  getDocs,
  glossariesRef,
  glossaryDraftsRef,
  glossaryRef,
  glossaryTermsCollectionRef,
  glossaryTermsRef,
} from '../firebase'
import { addDefaultMacos, addMacros, replaceMacros } from './macros'
import { addReferences, replaceReferences } from './references'

// 新規投稿と更新の両方を、新しい順に並べる
export const recentGlossariesRef = (size: number) =>
  query(glossariesRef, orderBy('updated_by_user_at', 'desc'), limit(size))

export const createGlossaryDraft = async (
  uid: string,
  publicationRef?: DocRef<Glossary>
) => {
  const batch = new Batch()
  const now = Timestamp.now()
  if (publicationRef) {
    const publicationSnapshot = await getDoc(publicationRef)
    const publication = publicationSnapshot.data()
    if (!publication) throw new Error('The publication is not found.')
    const ref = batch.add(glossaryDraftsRef, {
      ...publication,
      publication_id: publication.id,
      created_by_user_at: now,
      updated_by_user_at: now,
    })
    await addMacros(publicationRef, ref, batch)
    await addReferences(publicationRef, ref, batch)
    await batch.commit()
    return ref
  } else {
    const ref = batch.add(glossaryDraftsRef, {
      created_by: uid,
      last_updated_by: uid,
      category_id: undefined,
      terms: [],
      summary: '',
      detail: '',
      created_by_user_at: now,
      updated_by_user_at: now,
    })
    await addDefaultMacos(uid, ref, batch)
    await batch.commit()
    return ref
  }
}

const setGlossaryTerms = (
  glossaryId: Glossary['id'],
  categoryId: Glossary['category_id'],
  terms: Term[],
  batch: Batch
) => {
  batch.create(glossaryTermsRef(glossaryId), {
    category_id: categoryId,
    terms,
    displays: terms.map((term) => term.display),
  })
}

export const publishGlossary = async (draft: GlossaryDraft) => {
  const now = Timestamp.now()
  if (draft.publication_id !== undefined) {
    const batch = new Batch()
    const publicationRef = glossaryRef(draft.publication_id)
    batch.update(publicationRef, {
      last_updated_by: draft.last_updated_by,
      terms: draft.terms,
      category_id: draft.category_id,
      summary: draft.summary,
      detail: draft.detail,
      updated_by_user_at: now,
    })
    await replaceMacros(draft.ref, publicationRef, batch)
    await replaceReferences(draft.ref, publicationRef, batch)
    setGlossaryTerms(publicationRef.id, draft.category_id, draft.terms, batch)
    await batch.delete(draft.ref)
    await batch.commit()
    return publicationRef
  } else {
    const batch = new Batch()
    const publicationRef = batch.add(glossariesRef, {
      created_by: draft.created_by,
      last_updated_by: draft.last_updated_by,
      terms: draft.terms,
      category_id: draft.category_id,
      summary: draft.summary,
      detail: draft.detail,
      created_by_user_at: now,
      updated_by_user_at: now,
    })

    await addMacros(draft.ref, publicationRef, batch)
    await addReferences(draft.ref, publicationRef, batch)
    setGlossaryTerms(publicationRef.id, draft.category_id, draft.terms, batch)
    await batch.delete(draft.ref)
    await batch.commit()
    return publicationRef
  }
}

export const deleteGlossary = async (glossaryRef: DocRef<Glossary>) => {
  const batch = new Batch()
  await batch.delete(glossaryRef)
  await batch.delete(glossaryTermsRef(glossaryRef.id), [])
  await batch.commit()
}

export const categoryGlossaryTermsRef = (categoryId: string) =>
  query(glossaryTermsCollectionRef, where('category_id', '==', categoryId))

// 用語解説ごとの用語を、読みの順に並べた1つの一覧にする
export const sortTermsWithGlossaryInfo = (
  glossaryTermsList: GlossaryTerms[]
): TermWithGlossaryInfo[] =>
  glossaryTermsList
    .flatMap((glossaryTerms) =>
      glossaryTerms.terms.map((term) => ({
        // getStaticProps の props に undefined を含められないので、カテゴリが無いときはキーごと省く
        ...(glossaryTerms.category_id !== undefined && {
          category_id: glossaryTerms.category_id,
        }),
        glossary_id: glossaryTerms.id,
        display: term.display,
        reading: term.reading,
      }))
    )
    .sort((a, b) =>
      a.reading.toLocaleUpperCase() < b.reading.toLocaleUpperCase() ? -1 : 1
    )

// array-contains-any に渡せる値の数の上限（firebase 9.14 では 10）
const ARRAY_CONTAINS_ANY_LIMIT = 10

// displays のどれかを用語に持つ glossary_terms を探す（array-contains-any の上限ごとに分けて問い合わせる）
const findGlossaryTermsByDisplays = async (displays: string[]) => {
  const uniqueDisplays = Array.from(new Set(displays))
  const chunks: string[][] = []
  for (let i = 0; i < uniqueDisplays.length; i += ARRAY_CONTAINS_ANY_LIMIT) {
    chunks.push(uniqueDisplays.slice(i, i + ARRAY_CONTAINS_ANY_LIMIT))
  }
  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      getDocs(
        query(
          glossaryTermsCollectionRef,
          where('displays', 'array-contains-any', chunk)
        )
      )
    )
  )
  const glossaryTermsList = new Map<string, GlossaryTerms>()
  snapshots.forEach((snapshot) =>
    snapshot.docs.forEach((doc) => glossaryTermsList.set(doc.id, doc.data()))
  )
  return Array.from(glossaryTermsList.values())
}

/**
 * 用語（display）から用語解説のIDを探す
 * 同じ用語を持つ用語解説が複数あるときは、IDの順で最初のもの（クエリの既定の並び順）を使う
 */
export const findGlossaryIdsByDisplays = async (displays: string[]) => {
  const glossaryTermsList = await findGlossaryTermsByDisplays(displays)
  const glossaryIds = new Map<string, string>()
  glossaryTermsList
    .sort((a, b) => (a.id < b.id ? -1 : 1))
    .forEach((glossaryTerms) =>
      glossaryTerms.displays.forEach((display) => {
        if (displays.includes(display) && !glossaryIds.has(display))
          glossaryIds.set(display, glossaryTerms.id)
      })
    )
  return glossaryIds
}

export type GlossarySharingTerms = {
  glossaryId: string
  title: string
  sharedDisplays: string[]
}

/**
 * 同じ用語を持つ他の用語解説を探す。二重投稿を防ぐため、投稿の前に確かめる
 * excludeGlossaryId には更新する用語解説自身のIDを渡す
 */
export const findGlossariesSharingTerms = async (
  terms: Term[],
  excludeGlossaryId?: string
): Promise<GlossarySharingTerms[]> => {
  const displays = terms.map((term) => term.display)
  const glossaryTermsList = await findGlossaryTermsByDisplays(displays)
  return glossaryTermsList
    .filter((glossaryTerms) => glossaryTerms.id !== excludeGlossaryId)
    .map((glossaryTerms) => ({
      glossaryId: glossaryTerms.id,
      title: glossaryTerms.terms[0]?.display ?? '用語未設定',
      sharedDisplays: displays.filter((display) =>
        glossaryTerms.displays.includes(display)
      ),
    }))
}
